/**
 * Per-view state machine for the widget.
 *
 * Each agent is one of four views (`site-agent`, `chat-button`,
 * `engage`, `rail`) with its own state machine. Visual layouts
 * (ICON / INPUT / ENGAGE / EXPANDED / SIDEBAR / FULL) are downstream
 * of (view, state) — `stateToLayout()` maps them.
 *
 * Reference: `agent-flows.md`. Treat this file as the canonical
 * implementation of that spec. Don't add ad-hoc layout transitions in
 * ui.tsx — route through `transition()` so every view gets the
 * same minimize / close / restore semantics.
 *
 * Experience is modeled as an orthogonal overlay flag (`experienceActive`)
 * because it can ride on top of any view. When active, the visual
 * layer renders FULL regardless of the underlying state; when it ends,
 * we fall back to that state's normal layout.
 */
import { LAYOUTS, type Layout } from './layouts';

export type AgentView = 'site-agent' | 'chat-button' | 'engage' | 'rail' | 'unbound';

export type SiteAgentState = 'prompt' | 'conversation' | 'rail' | 'dock';
export type ChatButtonState = 'dock' | 'open';
export type EngageState = 'compact' | 'open' | 'dock';
export type RailState = 'open' | 'dock';

export type ViewState = SiteAgentState | ChatButtonState | EngageState | RailState;

export interface WidgetState {
  view: AgentView;
  state: ViewState;
  /** Experience runtime overlay — when true, renders FULL on top of `state`. */
  experienceActive: boolean;
  /** True once the visitor has sent their first message. Drives restore semantics. */
  chatStarted: boolean;
  /**
   * M5 (mobile UX program): an ACTIVE voice session minimized to the
   * co-pilot PiP / audio pill — orthogonal to view/state exactly like
   * `experienceActive` (the session rides on top of whatever altitude-1
   * state the widget returns to). Added dark in Phase 1; the PiP
   * transitions land in M5. Default false everywhere.
   */
  sessionMinimized: boolean;
}

export interface TransitionContext {
  /** Whether the device is a touch/phone viewport. Drives mobile fallbacks. */
  mobile: boolean;
  /** Whether agent has opted into mobile takeover via the per-agent flag. */
  mobileTakeoverAllowed: boolean;
}

export type TransitionAction =
  /** Visitor clicked the launcher / Dock FAB. Restore from dock. */
  | { type: 'open' }
  /**
   * M5 — minimize an ACTIVE voice session to the co-pilot PiP: applies
   * the view's normal minimize semantics AND flags sessionMinimized so
   * the PiP overlay rides on the resulting altitude-1 state.
   */
  | { type: 'minimizeSession' }
  /** M5 — PiP tap: restore the conversation surface + clear the flag. */
  | { type: 'restoreSession' }
  /** Visitor pressed minimize (—). Per spec: one tier within view. */
  | { type: 'minimize' }
  /** Visitor pressed close (X). Per spec: always to dock. */
  | { type: 'close' }
  /** First user message sent. Drives prompt → conversation, compact → open. */
  | { type: 'submit' }
  /** Visitor clicked a CTA / chip / talk-now in the compact Engage card. */
  | { type: 'engage' }
  /**
   * Visitor manually promoted the compact form (corner-expand button on
   * Site Agent's INPUT pill, etc.) without typing or pressing a chip.
   * Same destination as submit for state-machine purposes (prompt →
   * conversation, compact → open) but does NOT flip chatStarted —
   * no message was sent yet.
   */
  | { type: 'expand' }
  /** agent.navigate(url) or agent.highlight(selector) — moves Site Agent to rail. */
  | { type: 'agentNavigate' }
  /** agent.showContent(item) — enter experience overlay. */
  | { type: 'showContent' }
  /** agent.endContent() or user closes content. */
  | { type: 'endContent' }
  /**
   * Header "view as sidebar" button while in experience overlay. Peels
   * the overlay and lands the widget in the view's SIDEBAR-layout state
   * (site-agent → rail; chat-button / engage / rail → open). Gives the
   * visitor a way to keep the chat panel up after dismissing the
   * content viewer without falling all the way back to compact/dock.
   */
  | { type: 'experienceToSidebar' };

// ---------- Boot ----------

/**
 * Compute the initial state for a freshly-mounted widget. Honors:
 *   - view's natural boot state
 *   - mobile rail fallback (per spec, rail does NOT boot open on mobile)
 *   - resume-from-snapshot (history non-empty → engaged state)
 *   - mobile takeover cap (downgrades takeover-on-load layouts)
 */
export function bootStateFor(
  view: AgentView,
  ctx: TransitionContext & { resumeWithHistory: boolean },
): WidgetState {
  const base: Omit<WidgetState, 'state'> = {
    view,
    experienceActive: false,
    sessionMinimized: false,
    chatStarted: ctx.resumeWithHistory,
  };

  // Resume — visitor was mid-conversation. Restore engaged state for
  // every view. This is the same rule `transition({type:'open'})`
  // applies on dock-click after chatStarted=true.
  if (ctx.resumeWithHistory) {
    return { ...base, state: engagedStateFor(view) };
  }

  // Fresh visitor — natural boot per view.
  switch (view) {
    case 'site-agent':
      // On mobile without takeover opt-in, downgrade prompt to dock so
      // the host page is visible. The prompt-pill *itself* is a takeover
      // on small screens (greeter + input bar covers the bottom).
      if (ctx.mobile && !ctx.mobileTakeoverAllowed) {
        return { ...base, state: 'dock' };
      }
      return { ...base, state: 'prompt' };
    case 'chat-button':
      return { ...base, state: 'dock' };
    case 'unbound':
      // The keyed stage IS the presence — the widget state machine only
      // tracks dock; engagement (voice, speech card) lives in the stage.
      return { ...base, state: 'dock' };
    case 'engage':
      if (ctx.mobile && !ctx.mobileTakeoverAllowed) {
        return { ...base, state: 'dock' };
      }
      return { ...base, state: 'compact' };
    case 'rail':
      // Spec: rail doesn't boot `open` on mobile. Honor mobileTakeoverAllowed
      // as override so customers can still opt back in.
      if (ctx.mobile && !ctx.mobileTakeoverAllowed) {
        return { ...base, state: 'dock' };
      }
      return { ...base, state: 'open' };
  }
}

/** Per-view "engaged" state — used by restore-from-dock and resume. */
function engagedStateFor(view: AgentView): ViewState {
  switch (view) {
    case 'site-agent':
      return 'conversation';
    case 'chat-button':
      return 'open';
    case 'engage':
      return 'open';
    case 'rail':
      return 'open';
    case 'unbound':
      return 'dock';
  }
}

// ---------- Transitions ----------

export function transition(
  current: WidgetState,
  action: TransitionAction,
  ctx: TransitionContext,
): WidgetState {
  // M5 — session-minimize is orthogonal (like the experience overlay):
  // delegate to the view's own minimize/open semantics, then flag.
  if (action.type === 'minimizeSession') {
    return { ...transition(current, { type: 'minimize' }, ctx), sessionMinimized: true };
  }
  if (action.type === 'restoreSession') {
    return { ...transition(current, { type: 'open' }, ctx), sessionMinimized: false };
  }

  // Experience overlay actions are orthogonal to view state.
  if (action.type === 'showContent') {
    return { ...current, experienceActive: true };
  }
  if (action.type === 'endContent') {
    return { ...current, experienceActive: false };
  }

  // Header "view as sidebar" from the content viewer: peel the overlay
  // and land in the view's SIDEBAR-layout state. site-agent has a
  // dedicated `rail` state for this; the other three views' `open` is
  // already SIDEBAR.
  if (action.type === 'experienceToSidebar') {
    const peeled = { ...current, experienceActive: false };
    switch (current.view) {
      case 'site-agent':
        return { ...peeled, state: 'rail' };
      case 'chat-button':
      case 'engage':
      case 'rail':
        return { ...peeled, state: 'open' };
      case 'unbound':
        return { ...peeled, state: 'dock' };
    }
  }

  // Spec (state.ts header comment): minimize from experience → source's
  // idle state. The "source idle state" is whatever state the visitor
  // was on when they opened the content viewer — i.e. the peeled state
  // itself. Don't run the view's normal minimize on top of the peel, or
  // engage compact would double-step to dock and the visitor lands on
  // the chat button instead of back on the engage card.
  if (action.type === 'minimize' && current.experienceActive) {
    return { ...current, experienceActive: false };
  }

  // If experience is currently overlaid, most actions still need to
  // peel it off. The spec says: close from experience → source's dock;
  // minimize from experience → source's idle state (handled above).
  // Treat any non-experience action as ending content first, then
  // applying — EXCEPT conversational actions: typing a question while
  // content is open must never close the viewer (talking about the
  // content IS the product). Only explicit dismiss/navigation peels.
  const keepsExperience =
    action.type === 'submit' || action.type === 'expand' || action.type === 'engage';
  const peeled: WidgetState =
    current.experienceActive && !keepsExperience
      ? { ...current, experienceActive: false }
      : current;

  switch (current.view) {
    case 'site-agent':
      return transitionSiteAgent(peeled, action, ctx);
    case 'chat-button':
      return transitionChatButton(peeled, action);
    case 'engage':
      return transitionEngage(peeled, action, ctx);
    case 'rail':
      return transitionRail(peeled, action, ctx);
    case 'unbound':
      // No chrome: every action lands back on dock; chatStarted flips on
      // submit so resume knows a conversation happened. Experience
      // overlays (railless, PR4) ride experienceActive untouched here.
      return action.type === 'submit'
        ? { ...peeled, state: 'dock', chatStarted: true }
        : { ...peeled, state: 'dock' };
  }
}

function transitionSiteAgent(
  current: WidgetState,
  action: TransitionAction,
  _ctx: TransitionContext,
): WidgetState {
  const state = current.state as SiteAgentState;
  switch (action.type) {
    case 'close':
      return { ...current, state: 'dock' };
    case 'open':
      // Dock click → idle or engaged depending on chat history.
      return {
        ...current,
        state: current.chatStarted ? 'conversation' : 'prompt',
      };
    case 'minimize':
      // prompt → dock | conversation → prompt | rail → prompt (one-shot)
      if (state === 'prompt') return { ...current, state: 'dock' };
      if (state === 'conversation') return { ...current, state: 'prompt' };
      if (state === 'rail') return { ...current, state: 'prompt' };
      return current;
    case 'submit':
      // First user message → conversation (covers prompt + rail).
      if (state === 'prompt' || state === 'rail') {
        return { ...current, state: 'conversation', chatStarted: true };
      }
      return { ...current, chatStarted: true };
    case 'expand':
      // Visitor manually expanded the compact pill without typing.
      // Same destination as submit (prompt → conversation) but
      // chatStarted stays false — no message sent yet.
      if (state === 'prompt') return { ...current, state: 'conversation' };
      return current;
    case 'agentNavigate':
      // agent.navigate / agent.highlight while in conversation → rail.
      // From `prompt` we're not in chat yet, so leave it alone.
      if (state === 'conversation') return { ...current, state: 'rail' };
      return current;
    case 'engage':
      // No `compact` state on Site Agent — ignore.
      return current;
    // minimizeSession/restoreSession are handled by the transition()
    // wrapper before dispatch — unreachable here, listed for exhaustiveness.
    case 'experienceToSidebar':
    case 'showContent':
    case 'endContent':
    case 'minimizeSession':
    case 'restoreSession':
      return current; // handled in transition()
  }
}

function transitionChatButton(
  current: WidgetState,
  action: TransitionAction,
): WidgetState {
  switch (action.type) {
    case 'close':
    case 'minimize':
      return { ...current, state: 'dock' };
    case 'open':
      return { ...current, state: 'open' };
    case 'submit':
      return { ...current, chatStarted: true };
    case 'expand':
      // Chat Button has no compact-to-open promotion path — open IS
      // the engaged state. Stay put.
      return current;
    // minimizeSession/restoreSession are handled by the transition()
    // wrapper before dispatch — unreachable here, listed for exhaustiveness.
    case 'agentNavigate':
    case 'engage':
    case 'experienceToSidebar':
    case 'showContent':
    case 'endContent':
    case 'minimizeSession':
    case 'restoreSession':
      return current;
  }
}

function transitionEngage(
  current: WidgetState,
  action: TransitionAction,
  _ctx: TransitionContext,
): WidgetState {
  const state = current.state as EngageState;
  switch (action.type) {
    case 'close':
      return { ...current, state: 'dock' };
    case 'open':
      return {
        ...current,
        state: current.chatStarted ? 'open' : 'compact',
      };
    case 'minimize':
      // open → compact | compact → dock
      if (state === 'open') return { ...current, state: 'compact' };
      if (state === 'compact') return { ...current, state: 'dock' };
      return current;
    case 'engage':
      // CTA click / talk-now from compact → open.
      if (state === 'compact') return { ...current, state: 'open' };
      return current;
    case 'submit':
      // First user message from compact → open. From open, stay.
      return {
        ...current,
        state: state === 'compact' ? 'open' : state,
        chatStarted: true,
      };
    case 'expand':
      // Manual compact → open promotion without typing. Mirrors submit's
      // visual but leaves chatStarted false.
      if (state === 'compact') return { ...current, state: 'open' };
      return current;
    case 'agentNavigate':
      return current; // Engage stays put on navigate.
    // minimizeSession/restoreSession are handled by the transition()
    // wrapper before dispatch — unreachable here, listed for exhaustiveness.
    case 'experienceToSidebar':
    case 'showContent':
    case 'endContent':
    case 'minimizeSession':
    case 'restoreSession':
      return current;
  }
}

function transitionRail(
  current: WidgetState,
  action: TransitionAction,
  _ctx: TransitionContext,
): WidgetState {
  switch (action.type) {
    case 'close':
    case 'minimize':
      // Rail always collapses to dock — every view can be closed back
      // to the FAB. The point of a view is where it starts, not whether
      // visitors are allowed to dismiss it.
      return { ...current, state: 'dock' };
    case 'open':
      return { ...current, state: 'open' };
    case 'submit':
      return { ...current, chatStarted: true };
    case 'expand':
      // Rail has no compact tier — open IS the engaged state. Stay.
      return current;
    case 'agentNavigate':
      return current; // Rail stays put on navigate.
    // minimizeSession/restoreSession are handled by the transition()
    // wrapper before dispatch — unreachable here, listed for exhaustiveness.
    case 'engage':
    case 'experienceToSidebar':
    case 'showContent':
    case 'endContent':
    case 'minimizeSession':
    case 'restoreSession':
      return current;
  }
  return current;
}

// ---------- (view, state) → visual Layout ----------

/**
 * Map an internal state to the visual layout the widget renders.
 * Experience overlay short-circuits to FULL.
 *
 * Today we use a shared SIDEBAR visual for every view's "open /
 * rail" state (Skyscraper-shared). The state machine still distinguishes
 * them internally — minimize / close / restore go to different places —
 * but the rendered chrome is the same. See view-model decisions for
 * why we deferred per-view open-state chrome.
 */
export function stateToLayout(s: WidgetState): Layout {
  if (s.experienceActive) return LAYOUTS.FULL;

  switch (s.view) {
    case 'site-agent':
      switch (s.state as SiteAgentState) {
        case 'prompt':
          return LAYOUTS.INPUT;
        case 'conversation':
          return LAYOUTS.EXPANDED;
        case 'rail':
          return LAYOUTS.SIDEBAR;
        case 'dock':
          return LAYOUTS.ICON;
      }
      break;
    case 'chat-button':
      return s.state === 'open' ? LAYOUTS.SIDEBAR : LAYOUTS.ICON;
    case 'unbound':
      return LAYOUTS.ICON;
    case 'engage':
      switch (s.state as EngageState) {
        case 'compact':
          return LAYOUTS.ENGAGE;
        case 'open':
          return LAYOUTS.SIDEBAR;
        case 'dock':
          return LAYOUTS.ICON;
      }
      break;
    case 'rail':
      return s.state === 'open' ? LAYOUTS.SIDEBAR : LAYOUTS.ICON;
  }
  // Exhaustiveness fallback — should be unreachable.
  return LAYOUTS.ICON;
}

// ---------- Reverse mapping for back-compat ----------

/**
 * Best-effort guess of (view, state) from a legacy `Layout`. Used to
 * keep the old `data-showpilot-layout=...` boot attribute working — it
 * names a layout, we infer a view. Slice 1's migration backfilled
 * agent.view from the same heuristic, so this is the in-memory mirror.
 */
export function viewFromLegacyLayout(layout: Layout): {
  view: AgentView;
  state: ViewState;
} {
  switch (layout) {
    case LAYOUTS.ICON:
      return { view: 'chat-button', state: 'dock' };
    case LAYOUTS.INPUT:
      return { view: 'site-agent', state: 'prompt' };
    case LAYOUTS.EXPANDED:
      return { view: 'site-agent', state: 'conversation' };
    case LAYOUTS.SIDEBAR:
      return { view: 'rail', state: 'open' };
    case LAYOUTS.FULL:
      // Experience as a boot layout is legacy — treat as site-agent
      // with experience overlay, so the visitor lands in content view
      // and the agent it sources from is a site-agent.
      return { view: 'site-agent', state: 'conversation' };
    case LAYOUTS.ENGAGE:
      return { view: 'engage', state: 'compact' };
  }
}
